/**
 * Contextual Create Task Drawer
 *
 * Wraps the Create Task drawer for the Customers, Transactions and Ageing Analysis screens:
 * the customer and task type come from the source screen and are locked, while the title and
 * description are pre-filled from the selected transaction(s) and stay editable.
 */

import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  type CreateTaskCustomer,
  CreateTaskDrawer,
  type NewTaskData,
} from '@/components/task-board/create-task-drawer';
import { mapFrontendTaskTypeToApi, useCreateTaskMutation } from '@/hooks/useTasksData';
import { formatOutstandingByCurrency } from '@/lib/transaction-outstanding-summary';
import { formatCurrency } from '@/lib/utils';
import type { Transaction } from '@/types/transaction';

interface ContextualCreateTaskDrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customer: CreateTaskCustomer;
  /** Transactions the task follows up. Empty for a task raised from the Customers screen. */
  readonly transactions?: Transaction[];
  readonly baseCurrency?: string;
  readonly onCreated?: () => void;
}

function buildTransactionDefaults(transactions: Transaction[], baseCurrency: string) {
  if (transactions.length === 1) {
    const transaction = transactions[0];
    const amount = formatCurrency(
      transaction.outstandingAmount ?? transaction.amount,
      transaction.currency || baseCurrency,
    );
    const dueDate = transaction.dueDate ? format(new Date(transaction.dueDate), 'dd/MM/yyyy') : 'not set';

    return {
      title: `Follow up: ${transaction.referenceNumber}`,
      description: `Transaction ${transaction.referenceNumber} — outstanding ${amount}, due ${dueDate}.`,
    };
  }

  const references = transactions.map((x) => x.referenceNumber).join(', ');

  return {
    title: `Follow up: ${transactions.length} transactions`,
    description: `Transactions ${references} — outstanding ${formatOutstandingByCurrency(transactions, baseCurrency)}.`,
  };
}

export function ContextualCreateTaskDrawer({
  open,
  onOpenChange,
  customer,
  transactions = [],
  baseCurrency = 'GBP',
  onCreated,
}: ContextualCreateTaskDrawerProps) {
  const createTaskMutation = useCreateTaskMutation();

  const isTransactionTask = transactions.length > 0;
  const defaults = isTransactionTask ? buildTransactionDefaults(transactions, baseCurrency) : undefined;

  const handleCreateTask = (task: NewTaskData) => {
    createTaskMutation.mutate(
      {
        title: task.title,
        description: task.description || null,
        userAssignedId: parseInt(task.assignedToId, 10),
        dueDate: format(task.dueDate, 'yyyy-MM-dd'),
        customerId: parseInt(customer.id, 10),
        taskType: mapFrontendTaskTypeToApi(task.type),
        transactionId: transactions.length === 1 ? transactions[0].id : null,
      },
      {
        onSuccess: () => {
          toast.success('Task created successfully', { position: 'bottom-right' });
          onCreated?.();
        },
        onError: (error) => {
          toast.error(`Failed to create task: ${error.message}`, { position: 'bottom-right' });
        },
      },
    );
  };

  return (
    <CreateTaskDrawer
      open={open}
      onOpenChange={onOpenChange}
      onCreateTask={handleCreateTask}
      isSubmitting={createTaskMutation.isPending}
      prefilledCustomer={customer}
      taskType={isTransactionTask ? 'transaction' : 'customer_action'}
      defaultTitle={defaults?.title}
      defaultDescription={defaults?.description}
    />
  );
}
